import { DateTime, DateTimeOptions, FixedOffsetZone, IANAZone, LocaleOptions, SystemZone, ToISODateOptions, Zone } from 'luxon'
import { SimplisafeTimeZoneName } from '../constants'
import { Location } from '../api/models'
import { LoggerName, namedLogger } from './logger'

const logger = namedLogger(LoggerName.dateUtils)

/** Luxon format for a numeric year-month-day date, e.g. 2025-01-31. */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd'

/**
 * The time zone used to frame the dates pertaining to the location.
 *
 * If the location does not have a time zone, or if the time zone it has is invalid, the system time zone is used.
 */
export function locationTimeZone(location: Location | undefined): Zone {
  return getTimeZoneByName(location?.address?.timeZone)
}

export const getUserLocale = (): string => {
  return navigator.language
}

type DateFormatOptions = ToISODateOptions & LocaleOptions

type TimestampFormatOptions = DateTimeOptions & {
  timeZoneName?: SimplisafeTimeZoneName
  format?: string
}

export type RelativeOffset = {
  years?: number
  months?: number
  weeks?: number
  days?: number
}

type RangeCommon = {
  fieldLabel?: string
  required?: boolean
  message?: string
}

export type RelativeRangeConfig = RangeCommon & {
  type: 'relative'
  minOffset: RelativeOffset
  maxOffset: RelativeOffset
}

export type AbsoluteRangeConfig = RangeCommon & {
  type: 'absolute'
  minDate: DateTime
  maxDate: DateTime
}

export type DateRangeConfig = RelativeRangeConfig | AbsoluteRangeConfig

export type DateRangeResult = {
  pickerProps: {
    minDate: DateTime
    maxDate: DateTime
  }
  validate: (value: DateTime | null) => true | string
}

export const formatIsoDate = (isoDateStr?: string, opts?: DateFormatOptions): string => {
  if (!isoDateStr) return ''

  if (hasTimeDelimiter(isoDateStr)) {
    if (dex.config.validateDatesWhileFormatting && hasTimezoneInformation(isoDateStr)) {
      throw Error('Invalid isoDateStr. An isoDateStr must be of the format "YYYY-MM-DD" and must NOT have a time zone specified.')
    } else {
      isoDateStr = dropTimeFromIsoTimestamp(isoDateStr)
    }
  }

  const locale = getUserLocale()

  return DateTime.fromISO(isoDateStr, { locale: opts?.locale ?? locale }).toFormat(opts?.format ?? 'D')
}

export const formatIsoTimestamp = (isoTimestampStr: string, opts?: TimestampFormatOptions): string => {
  if (!isoTimestampStr) return ''

  if (dex.config.validateDatesWhileFormatting && !hasTimeDelimiter(isoTimestampStr)) {
    if (!hasTimezoneInformation(isoTimestampStr)) {
      throw Error('Invalid isoTimestampStr. An isoTimestampStr must be of the format "YYYY-MM-DDThh:mm:ss.000Z" and must have a time zone specified.')
    }
  }

  const zone = opts?.zone ?? getTimeZoneByName(opts?.timeZoneName)

  const locale = getUserLocale()

  const date = DateTime.fromISO(isoTimestampStr, { zone, locale: opts?.locale ?? locale })

  if (!date.isValid) {
    throw new Error(`Invalid date string: ${isoTimestampStr}`)
  }

  return date.toFormat(opts?.format ?? 'f')
}

const hasTimeDelimiter = (dateStr: string) => dateStr.indexOf('T') >= 0

export const hasTimezoneInformation = (dateStr: string) => {
  const timeDelimiter = dateStr.indexOf('T')
  return (
    timeDelimiter >= 0 &&
    (dateStr.indexOf('Z', timeDelimiter) >= 0 || dateStr.indexOf('+', timeDelimiter) >= 0 || dateStr.indexOf('-', timeDelimiter) >= 0)
  )
}

export const dropTimeFromIsoTimestamp = <T extends string | null | undefined>(isoTimestampStr: T): T => {
  if (typeof isoTimestampStr === 'string') {
    const indexOfT = isoTimestampStr.indexOf('T')
    if (indexOfT >= 0) {
      return isoTimestampStr.substring(0, indexOfT) as T
    }
  }
  return isoTimestampStr
}

export const formatIsoTimestampForLocation = (isoTimestampStr: string, location: Location | undefined, opts?: TimestampFormatOptions) =>
  formatIsoTimestamp(isoTimestampStr, { format: 'f ZZZZ', ...opts, zone: locationTimeZone(location) })

const getTimeZoneByName = (timeZoneName?: string): IANAZone | FixedOffsetZone => {
  if (!timeZoneName) {
    return SystemZone.instance
  }

  const invalidTimeZoneNames: SimplisafeTimeZoneName[] = ['Unknown', 'Unresolved']
  if (invalidTimeZoneNames.includes(timeZoneName as SimplisafeTimeZoneName)) {
    return SystemZone.instance
  }

  const locationTimeZone = IANAZone.create(timeZoneName)
  if (!locationTimeZone.isValid) {
    logger.warn(`Invalid timezone: ${timeZoneName}. Defaulting to local time.`)
    return SystemZone.instance
  }

  return locationTimeZone
}

export const isWithinLastMs = (isoDate: string, windowMs: number): boolean =>
  DateTime.fromISO(isoDate) >= DateTime.now().minus({ milliseconds: windowMs })

export const toDatePicker = (dateStr: string | null | undefined): DateTime => DateTime.fromISO(dropTimeFromIsoTimestamp(dateStr) ?? '')

export const fromDatePicker = (dateTime: DateTime | null | undefined): string | undefined => dateTime?.toISODate() ?? undefined

/**
 * Formats seconds to MM:SS format
 */
export const formatSeconds = (timeInSeconds: number): string => {
  return DateTime.fromSeconds(timeInSeconds).toFormat('m:ss')
}

const toUtcDay = (value: DateTime): DateTime => value.toUTC().startOf('day')

const applyOffset = (base: DateTime, offset: RelativeOffset): DateTime =>
  base.plus({
    years: offset.years ?? 0,
    months: offset.months ?? 0,
    weeks: offset.weeks ?? 0,
    days: offset.days ?? 0
  })

const formatUtcDate = (value: DateTime): string => toUtcDay(value).toFormat('MM-dd-yyyy')

export const buildDateRange = (config: DateRangeConfig): DateRangeResult => {
  const fieldLabel = config.fieldLabel ?? 'Date'

  const bounds =
    config.type === 'relative'
      ? (() => {
          const todayUtc = DateTime.utc().startOf('day')
          return {
            minDate: toUtcDay(applyOffset(todayUtc, config.minOffset)),
            maxDate: toUtcDay(applyOffset(todayUtc, config.maxOffset))
          }
        })()
      : {
          minDate: toUtcDay(config.minDate),
          maxDate: toUtcDay(config.maxDate)
        }

  const configError = bounds.minDate > bounds.maxDate ? 'Date range configuration is invalid: min date is after max date.' : null

  const defaultOutOfRangeMessage = `Date must be between ${formatUtcDate(bounds.minDate)} and ${formatUtcDate(bounds.maxDate)}`

  return {
    pickerProps: bounds,
    validate: (value: DateTime | null) => {
      if (configError) {
        return configError
      }

      if (value === null) {
        return config.required ? `${fieldLabel} is required` : true
      }

      const normalizedValue = toUtcDay(value)
      const inRange = normalizedValue >= bounds.minDate && normalizedValue <= bounds.maxDate

      if (!inRange) {
        return config.message ?? defaultOutOfRangeMessage
      }

      return true
    }
  }
}
